<template>
  <div class="dysfunction-description" :style="descriptionStyles">
    <div class="dysfunction-description__icon icon">
      {{ dysfunctionIcon }}
    </div>

    <div class="dysfunction-description__title" :class="titleClasses">
      {{ dysfunctionTitle }}
    </div>

    <div class="dysfunction-description__subtitle dysfunction-subtitle ">
      <span class="dysfunction-subtitle__name">{{ dysfunctionLabel }}</span>
      <span class="dysfunction-subtitle__bullet">&bull;</span>
      <span class="dysfunction-subtitle__score"> {{ score }} /10 </span>
    </div>

    <div class="dysfunction-description__details u-typography-helvetica u-text u-text--m">
      {{ dysfunctionDetails }}
    </div>
  </div>
</template>

<script>
import kebabCase from 'lodash.kebabcase';

import {
  DYSFUNCTION_NAMES,
  DYSFUNCTION_LABEL_KEY,
  DYSFUNCTION_ICONS
} from '@/constants/dysfunctions';

const DYSFUNCTION_DETAIL_KEY = {
  [DYSFUNCTION_NAMES.OXIDATIVE_STRESS]: 'section.yourSkin.oxidativeStress.description',
  [DYSFUNCTION_NAMES.SKIN_APPEARANCE]: 'section.yourSkin.skinAppearance.description',
  [DYSFUNCTION_NAMES.SKIN_REDNESS]: 'section.yourSkin.skinRedness.description',
  [DYSFUNCTION_NAMES.SKIN_DRYNESS]: 'section.yourSkin.skinDryness.description',
  [DYSFUNCTION_NAMES.SKIN_OILINESS]: 'section.yourSkin.skinOiliness.description',
  [DYSFUNCTION_NAMES.SKIN_TEXTURE]: 'section.yourSkin.skinTexture.description',
  [DYSFUNCTION_NAMES.PIMPLES]: 'section.yourSkin.pimples.description',
  [DYSFUNCTION_NAMES.SKIN_PIGMENTATION]: 'section.yourSkin.skinPigmentation.description'
};

const DYSFUNCTION_SEVERITY_NAMES = {
  LOW: 'low',
  MODERATE: 'moderate',
  HIGH: 'high',
  SEVERE: 'severe'
};

const DYSFUNCTION_TITLE_KEY_CONFIG = {
  [DYSFUNCTION_NAMES.OXIDATIVE_STRESS]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.oxidativeStress.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]:
      'section.yourSkin.oxidativeStress.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.oxidativeStress.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.oxidativeStress.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.SKIN_APPEARANCE]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.skinAppearance.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]:
      'section.yourSkin.skinAppearance.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.skinAppearance.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.skinAppearance.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.SKIN_REDNESS]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.skinRedness.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]: 'section.yourSkin.skinRedness.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.skinRedness.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.skinRedness.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.SKIN_DRYNESS]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.skinDryness.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]: 'section.yourSkin.skinDryness.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.skinDryness.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.skinDryness.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.SKIN_OILINESS]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.skinOiliness.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]:
      'section.yourSkin.skinOiliness.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.skinOiliness.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.skinOiliness.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.SKIN_TEXTURE]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.skinTexture.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]: 'section.yourSkin.skinTexture.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.skinTexture.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.skinTexture.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.PIMPLES]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.pimples.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]: 'section.yourSkin.pimples.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.pimples.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.pimples.dysfunctionTitle.severe'
  },
  [DYSFUNCTION_NAMES.SKIN_PIGMENTATION]: {
    [DYSFUNCTION_SEVERITY_NAMES.LOW]: 'section.yourSkin.skinPigmentation.dysfunctionTitle.low',
    [DYSFUNCTION_SEVERITY_NAMES.MODERATE]:
      'section.yourSkin.skinPigmentation.dysfunctionTitle.moderate',
    [DYSFUNCTION_SEVERITY_NAMES.HIGH]: 'section.yourSkin.skinPigmentation.dysfunctionTitle.high',
    [DYSFUNCTION_SEVERITY_NAMES.SEVERE]: 'section.yourSkin.skinPigmentation.dysfunctionTitle.severe'
  }
};

export default {
  name: 'DysfunctionDescriptionSlide',
  props: {
    dysfunctionName: {
      type: String,
      required: true
    },
    score: {
      type: Number,
      required: true
    }
  },
  computed: {
    dysfunctionIcon() {
      return DYSFUNCTION_ICONS[this.dysfunctionName];
    },
    descriptionStyles() {
      return {
        '--dysfunction-color': `var(--u-color-${kebabCase(this.dysfunctionName)})`
      };
    },
    dysfunctionTitle() {
      const dysfunctionTitleKeyConfig = DYSFUNCTION_TITLE_KEY_CONFIG[this.dysfunctionName];

      if (!dysfunctionTitleKeyConfig) {
        return '';
      }

      let titleKey;

      switch (true) {
        case this.score > 8: {
          titleKey = dysfunctionTitleKeyConfig[DYSFUNCTION_SEVERITY_NAMES.SEVERE];
          break;
        }
        case this.score > 6: {
          titleKey = dysfunctionTitleKeyConfig[DYSFUNCTION_SEVERITY_NAMES.HIGH];
          break;
        }
        case this.score > 3: {
          titleKey = dysfunctionTitleKeyConfig[DYSFUNCTION_SEVERITY_NAMES.MODERATE];
          break;
        }
        default: {
          titleKey = dysfunctionTitleKeyConfig[DYSFUNCTION_SEVERITY_NAMES.LOW];
        }
      }

      return this.$t(titleKey);
    },
    dysfunctionLabel() {
      return this.$t(DYSFUNCTION_LABEL_KEY[this.dysfunctionName]);
    },
    dysfunctionDetails() {
      return this.$t(DYSFUNCTION_DETAIL_KEY[this.dysfunctionName]);
    },
    titleClasses() {
      if (this.$windowWidth.value < 1920) {
        return ['u-typography-helvetica', 'u-text-display', 'u-text-display--s'];
      }

      return ['u-typography-helvetica', 'u-text-display', 'u-text-display--m'];
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/icon';

.dysfunction-description {
  color: var(--u-color-grey-900);

  &__icon {
    font-size: 54px;
    margin-bottom: 24px;
    color: var(--dysfunction-color);
  }

  &__title {
    color: var(--dysfunction-color);
    margin-bottom: 24px;
  }

  &__subtitle {
    margin-bottom: 24px;
  }

  &__details {
    color: var(--u-color-grey-600);
  }
}

.dysfunction-subtitle {
  font-family: var(--u-font-family-Helvetica);
  font-weight: bold;
  font-size: 18px;
  line-height: 23px;
  color: var(--u-color-black-5);

  &__bullet {
    color: var(--u-color-grey-600);
    margin: 0 16px;
  }
}
</style>
